// Contabilità dei mandati su due canali, imponibile e IVA. Replica la logica
// del BE (IvaChannelUtils e AmministrazioneService.calcolaCanali): va tenuta
// allineata a mano.

// SOLO_IMPONIBILE: importo imponibile ma escluso da IVA (es. rimborsi spese
// documentate). Vale solo per le rettifiche.
export type IvaMode = 'LORDO' | 'NETTO' | 'SOLO_IVA' | 'SOLO_IMPONIBILE';

export const ALIQUOTA_IVA = 22;

export const IVA_MODE_LABEL: Record<IvaMode, string> = {
    LORDO: 'IVA inclusa',
    NETTO: 'Senza IVA',
    SOLO_IVA: 'Solo IVA',
    SOLO_IMPONIBILE: 'Solo imponibile',
};
export const IVA_MODE_SHORT: Record<IvaMode, string> = {
    LORDO: 'IVA incl.',
    NETTO: 'No IVA',
    SOLO_IVA: 'Solo IVA',
    SOLO_IMPONIBILE: 'Solo imp.',
};

// Default NETTO sia per incassi sia per rettifiche: gli acconti incassati sono
// considerati solo imponibile. Cambia la ripartizione, non i totali.
export const DEFAULT_INCASSO_MODE: IvaMode = 'NETTO';
export const DEFAULT_RETTIFICA_MODE: IvaMode = 'NETTO';

export function parseIvaMode(raw: unknown, def: IvaMode): IvaMode {
    if (raw == null) return def;
    const v = String(raw).trim().toUpperCase();
    if (v === 'LORDO') return 'LORDO';
    if (v === 'NETTO') return 'NETTO';
    if (v === 'SOLO_IVA' || v === 'SOLOIVA') return 'SOLO_IVA';
    if (v === 'SOLO_IMPONIBILE' || v === 'SOLOIMPONIBILE') return 'SOLO_IMPONIBILE';
    return def;
}

/** Arrotondamento HALF_UP simmetrico a 2 decimali (evita −0 e il caso 1.005). */
export function round2(x: number): number {
    if (!isFinite(x)) return 0;
    return (Math.sign(x) * Math.round(Math.abs(x) * 100 + 1e-9)) / 100;
}

export interface Split { imp: number; iva: number; }

export function splitIncasso(importo: number, mode: IvaMode): Split {
    const x = Number(importo) || 0;
    switch (mode) {
        case 'NETTO':
        case 'SOLO_IMPONIBILE': return { imp: round2(x), iva: 0 };
        case 'SOLO_IVA': return { imp: 0, iva: round2(x) };
        case 'LORDO':
        default: {
            const imp = round2(x / (1 + ALIQUOTA_IVA / 100));
            return { imp, iva: round2(x - imp) };
        }
    }
}

export function splitRettifica(importo: number, mode: IvaMode): Split {
    const x = Number(importo) || 0;
    switch (mode) {
        case 'NETTO':    return { imp: round2(x), iva: round2(x * (ALIQUOTA_IVA / 100)) };
        case 'SOLO_IVA': return { imp: 0, iva: round2(x) };
        case 'SOLO_IMPONIBILE': return { imp: round2(x), iva: 0 };
        case 'LORDO':
        default: {
            const imp = round2(x / (1 + ALIQUOTA_IVA / 100));
            return { imp, iva: round2(x - imp) };
        }
    }
}

/**
 * Incasso IVA inclusa su un dovuto che ha una quota senza IVA (spese esenti): la quota
 * esente si prende in proporzione (importo × esente / totale dovuto), il resto si scorpora.
 * Un incasso pari al totale dovuto salda entrambi i canali. Senza quota esente coincide
 * con splitIncasso.
 */
export function splitIncassoConEsente(importo: number, mode: IvaMode, esente: number, totaleDovuto: number): Split {
    if (mode !== 'LORDO' || !(esente > 0) || !(totaleDovuto > 0)) return splitIncasso(importo, mode);
    const x = Number(importo) || 0;
    const quotaEsente = esente >= totaleDovuto ? round2(x) : round2(x * esente / totaleDovuto);
    const s = splitIncasso(round2(x - quotaEsente), 'LORDO');
    return { imp: round2(quotaEsente + s.imp), iva: s.iva };
}

// Movimento creato da "Registra fattura": importo IVA inclusa. I vecchi non hanno ivaMode.
export const FATTURA_REGISTRATA_DESCR = 'Fattura registrata';

/** ivaMode di un incasso; senza campo vale il default, tranne la fattura registrata (LORDO). */
export function incassoIvaMode(m: { ivaMode?: unknown; descrizione?: unknown }): IvaMode {
    const raw = m?.ivaMode;
    if ((raw == null || String(raw).trim() === '')
        && typeof m?.descrizione === 'string' && m.descrizione.startsWith(FATTURA_REGISTRATA_DESCR)) {
        return 'LORDO';
    }
    return parseIvaMode(raw, DEFAULT_INCASSO_MODE);
}

export interface CanaliInput {
    imponibileBase: number;
    /** Quota della base esclusa da IVA (spese a rivalsa nei mandati a consuntivo). Default 0. */
    imponibileBaseEsente?: number;
    aggiustamenti: { importo: number; ivaMode?: unknown }[];
    movimenti: { importo: number; tipo: string; fatturato?: boolean; ivaMode?: unknown; descrizione?: unknown }[];
}

export interface Canali {
    imponibileBase: number; ivaBase: number;
    imponibileDovuto: number; ivaDovuta: number; totaleDovuto: number;
    imponibileIncassato: number; ivaIncassata: number; totaleIncassato: number;
    imponibileFatturato: number; ivaFatturata: number;
    residuoImponibile: number; residuoIva: number; residuoTotale: number;
    daFatturareImponibile: number; daFatturareIva: number;
    isImponibileSaldato: boolean; isIvaSaldata: boolean; isSaldato: boolean;
    percIncassato: number;
}

export function calcolaCanali(input: CanaliInput): Canali {
    const imponibileBase = round2(input.imponibileBase);
    const imponibileBaseEsente = round2(Number(input.imponibileBaseEsente) || 0);
    const ivaBase = round2((imponibileBase - imponibileBaseEsente) * (ALIQUOTA_IVA / 100));

    let imponibileDovuto = imponibileBase;
    let ivaDovuta = ivaBase;
    // Quota del dovuto senza IVA: base esente + rettifiche "Solo imponibile".
    let esenteDovuto = imponibileBaseEsente;
    for (const a of input.aggiustamenti || []) {
        const mode = parseIvaMode(a.ivaMode, DEFAULT_RETTIFICA_MODE);
        const s = splitRettifica(Number(a.importo) || 0, mode);
        imponibileDovuto = round2(imponibileDovuto + s.imp);
        ivaDovuta = round2(ivaDovuta + s.iva);
        if (mode === 'SOLO_IMPONIBILE') esenteDovuto = round2(esenteDovuto + s.imp);
    }
    esenteDovuto = Math.max(0, Math.min(esenteDovuto, imponibileDovuto));
    const totaleDovutoLordo = round2(imponibileDovuto + ivaDovuta);

    let imponibileIncassato = 0, ivaIncassata = 0, imponibileFatturato = 0, ivaFatturata = 0;
    for (const m of input.movimenti || []) {
        if (m.tipo !== 'entrata') continue;
        const s = splitIncassoConEsente(Number(m.importo) || 0, incassoIvaMode(m), esenteDovuto, totaleDovutoLordo);
        imponibileIncassato = round2(imponibileIncassato + s.imp);
        ivaIncassata = round2(ivaIncassata + s.iva);
        if (m.fatturato) {
            imponibileFatturato = round2(imponibileFatturato + s.imp);
            ivaFatturata = round2(ivaFatturata + s.iva);
        }
    }

    const residuoImponibile = round2(imponibileDovuto - imponibileIncassato);
    const residuoIva = round2(ivaDovuta - ivaIncassata);
    const totaleDovuto = round2(imponibileDovuto + ivaDovuta);
    const totaleIncassato = round2(imponibileIncassato + ivaIncassata);
    const residuoTotale = round2(residuoImponibile + residuoIva);

    return {
        imponibileBase, ivaBase,
        imponibileDovuto, ivaDovuta, totaleDovuto,
        imponibileIncassato, ivaIncassata, totaleIncassato,
        imponibileFatturato, ivaFatturata,
        residuoImponibile, residuoIva, residuoTotale,
        daFatturareImponibile: round2(imponibileDovuto - imponibileFatturato),
        daFatturareIva: round2(ivaDovuta - ivaFatturata),
        isImponibileSaldato: imponibileDovuto > 0 && residuoImponibile <= 0.005,
        isIvaSaldata: ivaDovuta <= 0 ? true : residuoIva <= 0.005,
        isSaldato: (imponibileDovuto > 0 && residuoImponibile <= 0.005) && (ivaDovuta <= 0 || residuoIva <= 0.005),
        percIncassato: totaleDovuto > 0 ? Math.min(100, (totaleIncassato / totaleDovuto) * 100) : 0,
    };
}
